<template>
  <div class="result-detail-container nav-content-inset">
    <ImageBar :title="result.examName" height="60px"/>
    <div class="result-content" v-loading="loading">
      <div v-if="isAdminView" class="admin-user-banner">
        <span>用户：{{ result.nickname || result.username || '—' }}</span>
        <span v-if="result.userUuid" class="admin-user-uuid">{{ result.userUuid }}</span>
        <el-button v-if="adminListPath" type="primary" link @click="router.push(adminListPath)">返回列表</el-button>
      </div>
      <div class="result-meta">
        <span class="time">用时：{{ formatDuration(result.totalTime) }}</span>
        <span class="date">完成时间：{{ formatDate(result.createdAt) }}</span>
        <div v-if="isShareView" class="action-buttons">
          <el-button type="primary" @click="viewExamDetail">我也测一次</el-button>
        </div>
        <div v-else-if="!isAdminView" class="action-buttons">
          <el-button type="primary" @click="retakeTest">重新测试</el-button>
          <el-button type="info" @click="viewHistory">测试历史</el-button>
          <el-button type="warning" @click="viewExamDetail">测试详情</el-button>
          <el-button @click="shareResult" :loading="sharing">{{ result.shareCode ? '复制分享链接' : '分享' }}</el-button>
          <el-button v-if="result.shareCode" type="danger" plain @click="revokeShare" :loading="revokingShare">关闭分享</el-button>
          <el-button type="success" @click="recalculateResult" :loading="recalculating">重新计算</el-button>
          <el-button type="danger" @click="deleteResult">删除结果</el-button>
        </div>
      </div>

      <div v-if="userBadge && !isAdminView" class="badge-banner">
        <span class="badge-banner__label">获得徽章</span>
        <ExamBadgeTag
          :tier="userBadge.badgeTier"
          :label="userBadge.badgeLabel"
          :exam-name="userBadge.examName || result.examName"
          :score-percent="userBadge.scorePercent"
        />
      </div>

      <div class="exam-description" v-if="result.examProfile">
        <h3>考试简介</h3>
        <p>{{ result.examProfile }}</p>
      </div>

      <div v-if="radarData.length > 0">
        <div v-if="result.questionsType === 'bar'" class="bar-chart-container">
          <div v-for="(item, index) in barData" :key="index" class="bar-item">
            <div class="bar-label left" :class="{ 'highlighted': item.isLeftHighlighted }">{{ item.leftLabel }}</div>
            <div class="bar-wrapper" :style="{ 
              backgroundColor: item.isRightHighlighted ? 'var(--el-color-primary-light-8)' : 'var(--el-color-primary)'
            }">
              <div class="bar" :style="{ 
                width: `${(item.value / maxBarValue) * 100}%`,
                backgroundColor: item.isRightHighlighted ? 'var(--el-color-primary)' : 'var(--el-color-primary-light-8)',
                borderTopRightRadius: '0',
                borderBottomRightRadius: '0'
              }">
                <span class="bar-value">{{ item.value }}</span>
              </div>
            </div>
            <div class="bar-label right" :class="{ 'highlighted': item.isRightHighlighted }">{{ item.rightLabel }}</div>
          </div>
        </div>
        <div v-else-if="radarData.length === 1" class="single-result">
          <h3>测试结果</h3>
          <div class="result-value">
            {{ radarData[0].value }} / {{ displayMaxScore(radarData[0].name) }}
          </div>
        </div>
        <div v-else-if="result.questionsType === 'options' || radarData.length > 1" class="chart-container">
          <v-chart class="radar-chart" :option="radarOption" autoresize />
        </div>

        <div v-if="summaryData.length > 0" class="summary-container">
          <h3>维度说明</h3>
          <div class="summary-cards summary-cards--mobile">
            <div v-for="(row, idx) in summaryData" :key="idx" class="summary-card">
              <div class="summary-card__dimension">{{ row.dimension }}</div>
              <div v-if="row.digest" class="summary-card__digest">{{ row.digest }}</div>
              <div class="summary-card__desc">{{ row.description }}</div>
            </div>
          </div>
          <div class="summary-table-wrap summary-table-wrap--desktop">
            <el-table :data="summaryData" style="width: 100%">
              <el-table-column prop="dimension" label="维度" width="120" />
              <el-table-column prop="digest" label="摘要" width="120" />
              <el-table-column prop="description" label="详细说明" />
            </el-table>
          </div>
        </div>
      </div>

      <div v-if="parsedAnswers.length > 0" class="result-answers">
        <h3 class="result-answers__title">答题明细</h3>
        <div v-for="(answer, index) in parsedAnswers" :key="index" class="answer-item">
          <span class="question-tag">{{ answer.question_tag }}</span>
          <span class="question-text">{{ answer.question_text }}</span>
          <span class="selected-option">{{ answer.option_text }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, computed, onMounted } from 'vue'
import { authHeaders } from '@/utils/http'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import ImageBar from './components/ImageBar.vue'
import ExamBadgeTag from '@/components/exam/ExamBadgeTag.vue'
import { formatDuration } from '@/utils/formatDuration'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { RadarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  RadarComponent
} from 'echarts/components'
import VChart from 'vue-echarts'

use([
  CanvasRenderer,
  RadarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  RadarComponent
])

export default {
  name: 'TestResultDetailView',
  components: {
    ImageBar,
    VChart,
    ExamBadgeTag
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const isAdminView = computed(() => route.meta.adminResult === true || route.meta.creatorResult === true)
    const isShareView = computed(() => route.name === 'ExamResultShare')
    const loading = ref(false)
    const sharing = ref(false)
    const revokingShare = ref(false)
    const userBadge = ref(null)
    const recalculating = ref(false)
    const deleting = ref(false)
    const result = ref({
      examName: '',
      totalTime: 0,
      createdAt: 0,
      answer: '',
      examResult: '',
      examProfile: '',
      examId: 0,
      questionsType: '',
      maxScore: '',
      summary: '',
      shareCode: '',
      username: '',
      nickname: '',
      userUuid: ''
    })
    const adminListPath = computed(() => {
      if (route.meta.creatorResult) {
        const examId = result.value.examId
        return examId ? `/creator/exam/${examId}/results` : '/creator/exams'
      }
      if (route.meta.adminResult) {
        const examId = route.query.examId || result.value.examId
        return examId ? `/admin/exam-results?examId=${examId}` : '/admin/exam-results'
      }
      return ''
    })

    const radarData = computed(() => {
      try {
        console.log('Raw examresult:', result.value.examResult)
        console.log('Raw questionsType:', result.value.questionsType)

        const examResult = JSON.parse(result.value.examResult || '{}')
        console.log('Parsed exam result:', examResult)
        const data = Object.entries(examResult).map(([key, value]) => ({
          name: key.replace('__', ''),
          value: value
        }))
        console.log('Transformed radar data:', data)
        return data
      } catch (e) {
        console.error('Error parsing exam result:', e)
        return []
      }
    })

    const maxScores = computed(() => {
      try {
        return JSON.parse(result.value.maxScore || '{}')
      } catch {
        return {}
      }
    })

    /** 维度展示名：去掉前缀数字（如 1题目得分 → 题目得分） */
    const formatDimensionName = (name) => {
      if (!name) return ''
      return String(name).replace(/^\d+/, '')
    }

    const displayMaxScore = (dimensionKey) => {
      const max = maxScores.value[dimensionKey]
      if (max != null && max !== '') return max
      return '—'
    }

    const radarOption = computed(() => {
      if (radarData.value.length === 0) return null
      
      return {
        radar: {
          indicator: radarData.value.map(item => ({
            name: item.name,
            max: maxScores.value[item.name] || 60
          })),
          axisName: {
            color: '#666',
            fontSize: 12
          }
        },
        series: [{
          type: 'radar',
          data: [{
            value: radarData.value.map(item => item.value),
            name: '测试结果',
            areaStyle: {
              color: 'rgba(64, 158, 255, 0.3)'
            },
            lineStyle: {
              color: '#409EFF'
            },
            itemStyle: {
              color: '#409EFF'
            },
            label: {
              show: true,
              formatter: function(params) {
                return params.value;
              },
              color: '#409EFF',
              fontSize: 12,
              fontWeight: 'bold'
            }
          }]
        }],
        tooltip: {
          trigger: 'item',
          formatter: function(params) {
            const data = params.data;
            const indicators = radarOption.value.radar.indicator;
            let result = '<div style="font-weight:bold">测试结果</div>';
            data.value.forEach((value, index) => {
              result += `<div>${indicators[index].name}: ${value}</div>`;
            });
            return result;
          }
        }
      }
    })

    const parsedAnswers = computed(() => {
      try {
        const answers = JSON.parse(result.value.answer)
        return answers.map(item => ({
          question_text: item.quest,
          option_text: item.option,
          question_tag: `题目 ${item.qid}`
        }))
      } catch (e) {
        console.error('Error parsing answers:', e)
        return []
      }
    })

    const barData = computed(() => {
      console.log('barData computed triggered')
      console.log('Current result value:', result.value)
      console.log('questionsType:', result.value.questionsType)
      console.log('examResult:', result.value.examResult)
      if (result.value.questionsType !== 'bar' || !result.value.examResult) {
        console.log('Returning empty array due to conditions not met')
        return []
      }
      try {
        const examResult = JSON.parse(result.value.examResult)
        console.log('Parsed examResult:', examResult)
        
        // Define MBTI dimension order
        const mbtiOrder = ['E-I', 'S-N', 'T-F', 'J-P']
        
        // Check if this is an MBTI test by looking for any of these keys
        const isMBTI = Object.keys(examResult).some(key => mbtiOrder.includes(key))
        
        let data = Object.entries(examResult).map(([key, value]) => {
          const parts = key.split('-')
          const maxValue = maxScores.value[key] || 60
          const isRightHighlighted = value > maxValue / 2
          return {
            leftLabel: parts[0],
            rightLabel: parts[1] || '',
            value: value,
            maxValue: maxValue,
            isRightHighlighted: isRightHighlighted,
            isLeftHighlighted: !isRightHighlighted,
            key: key
          }
        })

        // If this is an MBTI test, sort the data according to the specified order
        if (isMBTI) {
          data = data.sort((a, b) => {
            const aIndex = mbtiOrder.indexOf(a.key)
            const bIndex = mbtiOrder.indexOf(b.key)
            return aIndex - bIndex
          })
        }

        console.log('Transformed bar data:', data)
        return data
      } catch (e) {
        console.error('Error parsing exam result:', e)
        return []
      }
    })

    const maxBarValue = computed(() => {
      if (barData.value.length === 0) return 0
      return Math.max(...barData.value.map(item => item.maxValue))
    })

    const summaryData = computed(() => {
      try {
        const summary = JSON.parse(result.value.summary || '{}')
        return Object.entries(summary).map(([key, value]) => ({
          dimension: key,
          digest: value.digest,
          description: value.description
        }))
      } catch (e) {
        console.error('Error parsing summary:', e)
        return []
      }
    })

    const loadUserBadge = async (examId) => {
      if (!examId || isAdminView.value) {
        userBadge.value = null
        return
      }
      try {
        const response = await axios.post('/api/v1/exam/badge/user-list', { page: 1, pageSize: 100 }, { headers: authHeaders() })
        if (response.data.code === 200) {
          const badges = response.data.data?.badges || []
          userBadge.value = badges.find(b => b.examId === examId) || null
        }
      } catch (e) {
        console.error('load badge failed', e)
      }
    }

    const copyText = async (text) => {
      try {
        await navigator.clipboard.writeText(text)
      } catch {
        const input = document.createElement('textarea')
        input.value = text
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }
    }

    const shareLinkOf = (code) => `${window.location.origin}/exam/share/${code}`

    const shareResult = async () => {
      try {
        sharing.value = true
        let code = result.value.shareCode
        if (!code) {
          const resultId = parseInt(route.params.id)
          const response = await axios.post('/api/v1/result/share/create', { resultId }, { headers: authHeaders() })
          if (response.data.code !== 200) {
            ElMessage.error(response.data.message || '分享失败')
            return
          }
          code = response.data.data?.shareCode || ''
          result.value.shareCode = code
        }
        if (!code) {
          ElMessage.error('分享失败')
          return
        }
        await copyText(shareLinkOf(code))
        ElMessage.success('分享链接已复制，任何人打开都能看到这份结果')
      } catch (error) {
        console.error('share result failed', error)
        ElMessage.error('分享失败')
      } finally {
        sharing.value = false
      }
    }

    const revokeShare = async () => {
      try {
        await ElMessageBox.confirm('关闭后，已发出的分享链接将无法再打开。', '关闭分享', {
          confirmButtonText: '关闭分享',
          cancelButtonText: '取消',
          type: 'warning'
        })
        revokingShare.value = true
        const resultId = parseInt(route.params.id)
        const response = await axios.post('/api/v1/result/share/revoke', { resultId }, { headers: authHeaders() })
        if (response.data.code === 200) {
          result.value.shareCode = ''
          ElMessage.success('已关闭分享')
        } else {
          ElMessage.error(response.data.message || '关闭分享失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('关闭分享失败')
        }
      } finally {
        revokingShare.value = false
      }
    }

    const fetchResult = async () => {
      try {
        loading.value = true
        let response
        if (isShareView.value) {
          const shareCode = String(route.params.code || '')
          response = await axios.post('/api/v1/result/share/get', { shareCode }, { skipAuth: true })
        } else {
          const resultId = parseInt(route.params.id)
          if (isNaN(resultId)) {
            ElMessage.error('无效的结果ID')
            return
          }
          const url = isAdminView.value
            ? '/api/v1/admin/exam/result/get'
            : '/api/v1/result/get'
          response = await axios.post(url, { resultId }, { headers: authHeaders() })
        }

        if (response.data.code === 200) {
          const data = response.data.data || {}
          result.value = isAdminView.value
            ? {
                examId: data.examId,
                examProfile: data.examProfile,
                examResult: data.examResult,
                answer: data.answer,
                isTemp: data.isTemp,
                totalTime: data.totalTime,
                examName: data.examName,
                questionsType: data.questionsType,
                maxScore: data.maxScore,
                summary: data.summary,
                createdAt: data.createdAt,
                username: data.username,
                nickname: data.nickname,
                userUuid: data.userUuid
              }
            : data
          console.log('Updated result value:', result.value)
          console.log('maxScore after update:', result.value.maxScore)
          if (!isShareView.value) {
            await loadUserBadge(result.value.examId)
          }
        } else if (isShareView.value) {
          ElMessage.error(response.data.message || '分享链接无效或已关闭')
        } else if (response.data.code === 401) {
          ElMessage.error('请先登录')
          goToLogin(router)
        } else {
          ElMessage.error(response.data.message || '获取测试结果失败')
        }
      } catch (error) {
        console.error('Error fetching result:', error)
        ElMessage.error(isShareView.value ? '暂时无法打开分享结果' : '获取测试结果失败')
      } finally {
        loading.value = false
      }
    }

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const retakeTest = () => {
      router.push(`/exam/take/${result.value.examId}`)
    }

    const viewHistory = () => {
      router.push(`/exam/result/list/${result.value.examId}`)
    }

    const viewExamDetail = () => {
      router.push(`/exam/${result.value.examId}`)
    }

    const recalculateResult = async () => {
      try {
        recalculating.value = true
        const token = localStorage.getItem('jwt_token')
        const resultId = parseInt(route.params.id)
        if (isNaN(resultId)) {
          ElMessage.error('无效的结果ID')
          return
        }
        const response = await axios.post('/api/v1/result/recalculate', {
          resultId: resultId
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          ElMessage.success('重新计算成功')
          // 重新获取结果数据
          await fetchResult()
        } else if (response.data.code === 401) {
          ElMessage.error('请先登录')
          goToLogin(router)
        } else {
          ElMessage.error(response.data.message || '重新计算失败')
        }
      } catch (error) {
        console.error('Error recalculating result:', error)
        ElMessage.error('重新计算失败')
      } finally {
        recalculating.value = false
      }
    }

    const deleteResult = async () => {
      try {
        // 使用 Element Plus 的确认对话框
        await ElMessageBox.confirm(
          '确定要删除这个测试结果吗？此操作不可恢复。',
          '警告',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          }
        )
        
        deleting.value = true
        const token = localStorage.getItem('jwt_token')
        const resultId = parseInt(route.params.id)
        if (isNaN(resultId)) {
          ElMessage.error('无效的结果ID')
          return
        }
        const response = await axios.post('/api/v1/result/delete', {
          resultId: resultId
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          // 返回上一页
          router.back()
        } else if (response.data.code === 401) {
          ElMessage.error('请先登录')
          goToLogin(router)
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') { // 忽略用户取消操作的情况
          console.error('Error deleting result:', error)
          ElMessage.error('删除失败')
        }
      } finally {
        deleting.value = false
      }
    }

    onMounted(() => {
      fetchResult()
    })

    return {
      router,
      isAdminView,
      isShareView,
      adminListPath,
      loading,
      sharing,
      revokingShare,
      userBadge,
      recalculating,
      deleting,
      result,
      parsedAnswers,
      formatDate,
      formatDuration,
      radarData,
      maxScores,
      displayMaxScore,
      formatDimensionName,
      radarOption,
      retakeTest,
      shareResult,
      revokeShare,
      viewHistory,
      viewExamDetail,
      recalculateResult,
      deleteResult,
      barData,
      maxBarValue,
      summaryData
    }
  }
}
</script>

<style scoped>
.admin-user-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: var(--el-fill-color-light);
  border-radius: 8px;
  font-size: 14px;
}
.badge-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 16px;
  padding: 12px 16px;
  background: var(--el-fill-color-light);
  border-radius: 8px;
}
.badge-banner__label {
  font-size: 14px;
  color: var(--el-text-color-secondary);
  font-weight: 500;
}
.admin-user-uuid {
  color: #909399;
  font-size: 12px;
  word-break: break-all;
}

.result-detail-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 10px;
  box-sizing: border-box;
  width: 100%;
}

.result-content {
  background-color: #fff;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow-x: hidden;
}

.result-meta {
  display: flex;
  align-items: center;
  gap: 15px;
  color: var(--el-text-color-secondary);
  font-size: 13px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.action-buttons .el-button {
  margin: 0;
}

.exam-description {
  margin-bottom: 20px;
  padding: 15px;
  background-color: var(--el-color-primary-light-9);
  border-radius: 4px;
}

.exam-description h3 {
  margin: 0 0 10px 0;
  color: var(--el-color-primary);
  font-size: 16px;
}

.exam-description p {
  margin: 0;
  color: var(--el-text-color-primary);
  line-height: 1.6;
  white-space: pre-wrap;
}

.chart-container {
  margin: 20px 0;
  height: 400px;
}

.radar-chart {
  width: 100%;
  height: 100%;
}

.result-answers {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-answers__title {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: var(--el-text-color-primary);
}

.answer-item {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--el-border-color-light);
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
}

.question-tag {
  padding: 2px 6px;
  background-color: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
  border-radius: 2px;
  white-space: nowrap;
}

.question-text {
  flex: 1 1 100%;
  min-width: 0;
  color: var(--el-text-color-primary);
  word-break: break-word;
}

.selected-option {
  padding: 2px 6px;
  background-color: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
  border-radius: 2px;
  white-space: nowrap;
}

.summary-cards--mobile {
  display: none;
}

.summary-table-wrap--desktop {
  width: 100%;
}

.summary-card {
  padding: 12px;
  margin-bottom: 10px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  background: var(--el-fill-color-blank);
}

.summary-card__dimension {
  font-weight: 600;
  font-size: 14px;
  color: var(--el-color-primary);
  margin-bottom: 6px;
}

.summary-card__digest {
  font-size: 13px;
  color: var(--el-text-color-regular);
  margin-bottom: 6px;
}

.summary-card__desc {
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.55;
  word-break: break-word;
}

@media (max-width: 767px) {
  .result-detail-container {
    padding: 0;
    max-width: 100%;
  }

  .result-content {
    padding: 12px;
    border-radius: 0;
    box-shadow: none;
  }

  .result-meta {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .action-buttons {
    margin-left: 0;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .action-buttons .el-button {
    width: 100%;
  }

  .admin-user-banner {
    flex-direction: column;
    align-items: flex-start;
  }

  .answer-item {
    flex-direction: column;
    align-items: stretch;
    font-size: 13px;
  }

  .question-tag,
  .selected-option {
    align-self: flex-start;
  }

  .question-text {
    flex: none;
    width: 100%;
  }

  .chart-container {
    height: min(72vw, 320px);
    margin: 12px 0;
  }

  .single-result {
    padding: 16px;
    margin: 12px 0;
  }

  .result-value {
    font-size: 36px;
  }

  .result-value::after {
    font-size: 18px;
  }

  .bar-chart-container {
    padding: 12px;
    margin: 12px 0;
  }

  .summary-container {
    padding: 12px;
    margin-top: 16px;
  }

  .summary-cards--mobile {
    display: block;
  }

  .summary-table-wrap--desktop {
    display: none;
  }

  .exam-description {
    padding: 12px;
  }
}

.single-result {
  text-align: center;
  margin: 20px 0;
  padding: 20px;
  background-color: var(--el-color-primary-light-9);
  border-radius: 8px;
}

.single-result h3 {
  margin: 0 0 10px 0;
  color: var(--el-text-color-primary);
  font-size: 16px;
}

.result-value {
  font-size: 48px;
  font-weight: bold;
  color: var(--el-color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.result-value::after {
  content: '分';
  font-size: 24px;
  font-weight: normal;
}

.bar-chart-container {
  margin: 20px 0;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.bar-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 10px;
}

.bar-label {
  min-width: 80px;
  color: var(--el-text-color-primary);
  font-size: 14px;
  transition: all 0.3s ease;
}

.bar-label.highlighted {
  font-weight: bold;
  font-size: 16px;
  color: var(--el-color-primary);
}

.bar-label.left {
  text-align: right;
}

.bar-label.right {
  text-align: left;
}

.bar-wrapper {
  flex: 1;
  height: 30px;
  border-radius: 15px;
  overflow: hidden;
  transition: background-color 0.3s ease;
}

.bar {
  height: 100%;
  border-radius: 15px 0 0 15px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 10px;
  transition: all 0.3s ease;
}

.bar-value {
  color: #fff;
  font-size: 12px;
  font-weight: bold;
}

@media (max-width: 767px) {
  .bar-item {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }

  .bar-label {
    min-width: auto;
    width: 100%;
    text-align: left;
    margin-bottom: 0;
  }

  .bar-label.left,
  .bar-label.right {
    text-align: left;
  }

  .bar-wrapper {
    width: 100%;
    height: 26px;
  }
}

.summary-container {
  margin-top: 30px;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.summary-container h3 {
  margin: 0 0 20px 0;
  color: var(--el-text-color-primary);
  font-size: 16px;
}

:deep(.el-table) {
  --el-table-border-color: var(--el-border-color-lighter);
  --el-table-header-bg-color: var(--el-fill-color-light);
}

:deep(.el-table__header) {
  th {
    background-color: var(--el-fill-color-light);
    color: var(--el-text-color-primary);
    font-weight: bold;
  }
}

:deep(.el-table__body) {
  td {
    padding: 12px 0;
  }
}
</style> 